import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton } from '@/shared/ui';

import { waitingAge } from '../model/ordering';
import type { CommitmentSummary } from '../model/types';
import { useDashboard } from '../queries/useCommitments';
import styles from './WorkList.module.css';

/**
 * Wspólny szkielet sześciu list z menu.
 *
 * ⭐ Listy są **osobnymi ekranami** (decyzja usera, 2026-08-03), bo ich wiersze są swoiste:
 * co innego znaczą, co innego pokazują i z czasem dostaną różne akcje oraz podglądy.
 * Wspólny jest tylko szkielet — nagłówek, stany brzegowe i rama tabeli. Kolumny i akcje
 * definiuje każdy ekran u siebie, więc rozejście się ich nie wymaga tu żadnej zmiany.
 *
 * ⚠ Wszystkie sześć jedzie z **jednego** zapytania `GET /api/dashboard`, które zwraca
 * pełne listy (nie podglądy) — kafel pulpitu jest przyciętą wersją dokładnie tych danych.
 * Skutek uboczny jest korzystny: przejście z kafla na listę nie kosztuje ani jednego
 * przelotu, bo pamięć podręczna jest wspólna.
 *
 * ⏳ Stronicowania nie ma świadomie: zakres to „elementy, za które odpowiadam", więc listy
 * są rzędu dziesiątek. Przy pierwszym koncie z tysiącem pozycji trzeba będzie wrócić.
 */
interface WorkListLayoutProps {
  titleKey: string;
  hintKey: string;
  emptyKey: string;
  count: number;
  /** Nagłówki kolumn — klucze i18n. Ostatnia kolumna bywa pusta (akcje). */
  columns: readonly string[];
  children: ReactNode;
}

export function WorkListLayout({
  titleKey,
  hintKey,
  emptyKey,
  count,
  columns,
  children,
}: WorkListLayoutProps) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const dashboard = useDashboard();

  if (dashboard.isPending) {
    return (
      <div className={styles['loading']} aria-busy="true">
        <Skeleton width="20rem" height="2rem" announce />
        <Skeleton height="3rem" />
        <Skeleton height="3rem" />
        <Skeleton height="3rem" />
      </div>
    );
  }

  if (dashboard.isError) {
    return (
      <div className={styles['failure']} role="alert">
        <p>{describeError(dashboard.error)}</p>
        <Button onClick={() => void dashboard.refetch()}>{t('common.action.retry')}</Button>
      </div>
    );
  }

  return (
    <div className={styles['screen']}>
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t(titleKey)}</h1>
        {count > 0 ? <span className={styles['count']}>{count}</span> : null}
      </header>

      <p className={styles['hint']}>{t(hintKey)}</p>

      {count === 0 ? (
        <div className={styles['wrap']}>
          <p className={styles['empty']}>{t(emptyKey)}</p>
        </div>
      ) : (
        <div className={styles['wrap']}>
          <table className={styles['table']}>
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <th key={column || `col-${index}`} scope="col">
                    {column ? t(column) : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{children}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Nazwa elementu prowadząca do niego **z otwartym panelem zobowiązań** (E1).
 *
 * Element jest tu jedyną informacją odróżniającą wiersze — listy zbierają zobowiązania
 * z kilkudziesięciu elementów naraz, więc bez tej kolumny wiersze byłyby nieodróżnialne.
 * To dokładnie ta kolumna, której panel E1 mieć nie może, bo tam element jest kontekstem.
 */
export function ElementLink({ id, title }: { id: number; title: string }) {
  return (
    <td className={styles['elementCell']}>
      <Link className={styles['link']} to={`/constellation/${id}?panel=commitments&el=${id}`} title={title}>
        {title}
      </Link>
    </td>
  );
}

/**
 * Wiek pozycji. Bierze **datę**, nie zobowiązanie, bo liczy się go też dla wiadomości
 * ze skrzynki — a te nie mają cyklu życia zobowiązania, tylko moment powstania.
 * Skąd wziąć datę dla zobowiązania, wie `waitingSince` z `model/ordering`.
 */
export function AgeCell({ since }: { since: string }) {
  const { t } = useTranslation();
  const age = waitingAge({ createdAtUtc: since } as CommitmentSummary, new Date());

  return (
    <td className={styles['muted']}>{t(`commitments.age.${age.unit}`, { count: age.value })}</td>
  );
}
